import { OverlayTrigger, Tooltip } from "react-bootstrap";
import pushHistory from "../../functions/pushHistory";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface ActionButtonProps {
	href?: string;
	history?: any;
	newWindow?: boolean;
	tooltip?: string | null;
	iconType?: string;
	icon?: string;
	show?: boolean;
	onClick?: (
		e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | MouseEvent,
	) => void;
	colour?: string;
	hoverColour?: string;
	pressed?: boolean;
	disabled?: boolean;
	toolTipPlacement?: any;
	rotateMode?: number;
	resetRotateAfter?: number;
	stateOverrides?: any;
	hideAfter?: number;
	simulatedClick?: null | {
		setState: React.Dispatch<React.SetStateAction<boolean>>;
		constant: boolean;
	};
	styleOverrides?: any;
	onTransitionEnd?: any;
}

/**
 * ActionButton is a customizable button component that can be used to trigger actions.
 *
 * @param {string} [href] - URL to navigate to when the button is clicked.
 * @param {any} [history] - History object for navigation.
 * @param {boolean} [newWindow] - If true, opens link in a new window.
 * @param {string|null} [tooltip] - Tooltip text to display on hover.
 * @param {string} [iconType] - Type of icon to display, default is "material-icons".
 * @param {string} [icon] - Icon content, default is "info".
 * @param {boolean} [show] - Visibility of the button.
 * @param {function} [onClick] - Callback function to execute on click.
 * @param {string} [colour] - Button color, default is white.
 * @param {string} [hoverColour] - Button color on hover, default is yellow.
 * @param {boolean} [pressed] - If true, applies pressed style.
 * @param {boolean} [disabled] - If true, disables the button.
 * @param {any} [toolTipPlacement] - Tooltip placement, default is "top".
 * @param {number} [rotateMode] - Mode of rotation animation on click.
 * @param {any} [stateOverrides] - Override states for rotation. (rotation, setRotation)
 * @param {number} [resetRotateAfter] - Time in ms after which the rotation resets.
 * @param {number} [hideAfter] - Time in ms after which the button hides.
 * @param {object|null} [simulatedClick] - Object to simulate a click.
 * @param {any} [styleOverrides] - Custom styles to apply.
 * @param {function} [onTransitionEnd] - Callback function to execute on transition end.
 *
 * This component supports rotation animations, hover effects, and customizable styles.
 */
const ActionButton: React.FC<ActionButtonProps> = ({
	href = "",
	history = null,
	newWindow = false,
	tooltip = null,
	iconType = "material-icons",
	icon = "info",
	show = true,
	onClick = (e) => history && pushHistory(history, e, newWindow),
	colour = "rgb(255, 255, 255)",
	hoverColour = "rgb(240, 208, 0)",
	pressed = false,
	disabled = false,
	toolTipPlacement = "top",
	rotateMode = 0,
	resetRotateAfter = 0,
	stateOverrides = {},
	hideAfter = 0,
	simulatedClick = null,
	styleOverrides = {},
	onTransitionEnd = () => {},
}) => {
	const [hover, setHover] = useState(false);
	const [rotationState, setRotationState] = useState(0);
	const [clicked, setClicked] = useState(false);
	const [hide, setHide] = useState(true);

	const rotation = stateOverrides.rotation || rotationState;
	const setRotation = stateOverrides.setRotation || setRotationState;
	const rotationResetRef = useRef<NodeJS.Timeout | null>(null);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const currentColour = hover || pressed ? hoverColour : colour;
	const currentColourRaw = currentColour.startsWith("#")
		? `${hexToRgb(currentColour.slice(0, 7)).join(",")}`
		: currentColour.replace("rgb(", "").replace(")", "");
	function hexToRgb(hex: string) {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
			: [0, 0, 0];
	}

	const opacityTransition =
		(disabled && show) || (!disabled && show)
			? ""
			: `all ${hideAfter}ms ease-in-out, `;

	const styles = {
		color: `${currentColour}`,
		borderColor: `${currentColour}`,
		backgroundColor: `rgba(${currentColourRaw}, 0.2)`,
		opacity: hideAfter > 0 && !show ? "0" : disabled ? "0.6" : "1",
		transition: `${opacityTransition}color 0.4s ease-in-out, border-color 0.4s ease-in-out, background-color 0.4s ease-in-out`,
		display: hide && !show ? "none" : "inline-block",
		...styleOverrides,
	};
	const iconStyles = {
		transform: `rotate(${rotation}deg) scale(${hover ? 1.33 : 1})`,
		transformOrigin: "center center",
		display: "inline-block",
		PointerEvents: "none",
		transition: "transform 0.4s ease-in-out",
	};

	const classNames = `${iconType} notranslate ${show ? "clickable" : ""} unselectable action-button ${disabled || !show ? "disabled" : ""}`;

	const handleClick = (
		e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
	) => {
		if (!disabled && show) {
			onClick(e);
			setClicked(true);
		}
	};

	useEffect(() => {
		if (simulatedClick?.constant === true && show && !disabled) {
			setClicked(true);
			onClick(new MouseEvent("click", { bubbles: true }));
		}

		if (simulatedClick) simulatedClick.setState(false);
	}, [simulatedClick, onClick, show, disabled]);

	useEffect(() => {
		if (hideAfter > 0 && show) {
			setHide(false);
		}

		if (!show) {
			setTimeout(() => setHide(true), hideAfter);
		}
	}, [hideAfter, show]);

	const handleRotate = useCallback(
		(mode) => {
			const rotate = Math.abs(rotateMode);
			const multiplier = rotate / rotateMode;
			// always 90 degrees
			if (rotate === 1)
				setRotation((prev) => prev + 90 * multiplier * mode);
			// rotate forward, then back
			if (rotate === 2)
				setRotation((prev) =>
					prev !== 0 ? 0 : prev + 180 * multiplier * mode,
				);
			// rotate fully
			if (rotate === 3)
				setRotation((prev) => prev + 360 * multiplier * mode);
			// wiggle
			if (rotate === 4) {
				setRotation((prev) => prev + 40 * multiplier * mode);
				setTimeout(
					() => setRotation((prev) => prev - 90 * multiplier * mode),
					400,
				);
				setTimeout(
					() => setRotation((prev) => prev + 50 * multiplier * mode),
					800,
				);
			}
			// half a rotation every time
			if (rotate === 5)
				setRotation((prev) => prev + 180 * multiplier * mode);
			// one third a rotation every time
			if (rotate === 6)
				setRotation((prev) => prev + 120 * multiplier * mode);
		},
		[rotateMode, setRotation],
	);

	// clear timeout at 0
	useEffect(() => {
		if (rotation === 0) {
			if (intervalRef.current) clearInterval(intervalRef.current);
			if (rotationResetRef.current)
				clearTimeout(rotationResetRef.current);
		}
	}, [rotation]);

	useEffect(() => {
		if (rotateMode !== 0 && clicked) {
			handleRotate(1);

			if (resetRotateAfter > 0) {
				// reset timeout and interval if they exist
				if (intervalRef.current) clearInterval(intervalRef.current);
				if (rotationResetRef.current)
					clearTimeout(rotationResetRef.current);

				rotationResetRef.current = setTimeout(() => {
					handleRotate(-1);
					intervalRef.current = setInterval(() => {
						handleRotate(-1);
					}, 1000);
				}, resetRotateAfter);
			}
		}
		setClicked(false);
	}, [
		clicked,
		rotateMode,
		setRotation,
		resetRotateAfter,
		handleRotate,
		rotation,
	]);

	useEffect(() => {
		if (disabled) {
			setHover(false);
		}
	}, [disabled]);

	useEffect(() => {
		if (!pressed) {
			setRotation(0);
			setHover(false);
		}
	}, [pressed, setRotation]);

	const anchorEl = (
		<a
			{...(href !== "" && { href: href })}
			onClick={handleClick}
			style={styles}
			onMouseEnter={() => !disabled && setHover(true)}
			onMouseLeave={() => !disabled && setHover(false)}
			className={classNames}>
			<span style={iconStyles} onTransitionEnd={onTransitionEnd}>
				{icon}
			</span>
		</a>
	);

	if (tooltip)
		return (
			<OverlayTrigger
				placement={toolTipPlacement}
				overlay={
					<Tooltip id="actionbutton-tooltip">
						<div>{tooltip}</div>
					</Tooltip>
				}>
				{anchorEl}
			</OverlayTrigger>
		);

	return anchorEl;
};

export default ActionButton;
